import { useState, useMemo, useCallback, ReactNode } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { TEXT } from './Typography';

interface DatePickerProps {
  label?: string;
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: ReactNode;
  placeholder?: string;
  className?: string;
  /** Minimum selectable date (YYYY-MM-DD). Dates before this are disabled. */
  min?: string;
  /** Maximum selectable date (YYYY-MM-DD). Dates after this are disabled. */
  max?: string;
  /**
   * Tailwind z-index class for the dropdown PANEL. Defaults to `z-50`.
   *
   * The panel is rendered in a portal on <body> (that is what Headless UI's
   * `anchor` prop does), so its z-index competes with every other body-level
   * layer rather than with its own parent. A caller that lives inside a
   * portalled popover of its own -- the roster planner's toolbar popovers sit
   * at z-[80], because they have to clear a z-50 fullscreen container -- must
   * raise this, or its dropdown opens BEHIND the thing that owns it.
   *
   * It REPLACES the default rather than being appended: two z-index classes on
   * one element are resolved by their order in the stylesheet, not in the class
   * attribute, so appending is not reliably a win.
   */
  panelZClass?: string;
}

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Parse YYYY-MM-DD to { year, month (0-based), day } or null */
function parseISO(iso: string): { year: number; month: number; day: number } | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m - 1, day: d };
}

/** Format date parts to YYYY-MM-DD */
function toISO(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Format YYYY-MM-DD for display (e.g. "26 Feb 2026") */
function formatDisplay(iso: string): string {
  const parsed = parseISO(iso);
  if (!parsed) return '';
  const d = new Date(parsed.year, parsed.month, parsed.day);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Get all calendar cells for a month (includes leading/trailing days from adjacent months) */
function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells: Array<{ day: number; month: number; year: number; isCurrentMonth: boolean }> = [];

  // Leading days from previous month
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, month: m, year: y, isCurrentMonth: false });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, isCurrentMonth: true });
  }

  // Trailing days to fill last row
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, month: nextMonth, year: nextYear, isCurrentMonth: false });
    }
  }

  return cells;
}

export default function DatePicker({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  icon,
  placeholder = 'Select date...',
  className = '',
  min,
  max,
  panelZClass = 'z-50',
}: DatePickerProps) {
  const parsed = parseISO(value);
  const today = useMemo(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
  }, []);

  // Calendar view state — default to selected date's month, or current month
  const [viewYear, setViewYear] = useState(parsed?.year ?? today.year);
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.month);

  // Sync view to selected value when it changes externally (e.g. OCR auto-set)
  const lastValue = useMemo(() => value, [value]);
  useMemo(() => {
    const p = parseISO(lastValue);
    if (p) {
      setViewYear(p.year);
      setViewMonth(p.month);
    }
  }, [lastValue]);

  const calendarDays = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);

  const goToPrevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const handleSelect = useCallback(
    (cell: { day: number; month: number; year: number }, close: () => void) => {
      onChange(toISO(cell.year, cell.month, cell.day));
      close();
    },
    [onChange],
  );

  const isToday = (cell: { day: number; month: number; year: number }) =>
    cell.day === today.day && cell.month === today.month && cell.year === today.year;

  const isSelected = (cell: { day: number; month: number; year: number }) =>
    parsed != null && cell.day === parsed.day && cell.month === parsed.month && cell.year === parsed.year;

  const isDateDisabled = (cell: { day: number; month: number; year: number }) => {
    const iso = toISO(cell.year, cell.month, cell.day);
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  };

  const displayIcon = icon ?? <CalendarDaysIcon className="h-4 w-4" />;

  return (
    <div className={className}>
      {label && (
        <label className={`block mb-1 leading-4 uppercase ${TEXT.labelMuted}`}>
          {label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      <Popover className="relative">
        {({ close }) => (
          <>
            <PopoverButton
              disabled={disabled}
              className={`flex items-center w-full h-[38px] rounded-md border ${
                error ? 'border-red-300' : 'border-gray-300'
              } bg-white py-2 px-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed text-left`}
            >
              <span className="text-gray-500 mr-2 flex-shrink-0">{displayIcon}</span>
              <span className={value ? 'text-gray-900 flex-1' : 'text-gray-500 flex-1'}>
                {value ? formatDisplay(value) : placeholder}
              </span>
            </PopoverButton>

            <PopoverPanel
              anchor="bottom start"
              className={`${panelZClass} mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 p-3 [--anchor-gap:4px]`}
            >
              {/* Month/Year header with navigation */}
              <div className="flex items-center justify-between mb-2">
                <Button variant="ghost" onClick={goToPrevMonth}>
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <span className={TEXT.bodyEmphasis}>
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </span>
                <Button variant="ghost" onClick={goToNextMonth}>
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>

              {/* Day name headers */}
              <div className="grid grid-cols-7 mb-1">
                {DAY_NAMES.map((d) => (
                  <div key={d} className="text-center text-2xs font-medium text-gray-500 uppercase py-1">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((cell, idx) => {
                  const selected = isSelected(cell);
                  const todayCell = isToday(cell);
                  const cellDisabled = isDateDisabled(cell);
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={cellDisabled}
                      onClick={() => handleSelect(cell, close)}
                      className={`
                        h-8 w-full text-xs rounded-md transition-colors
                        ${cellDisabled ? 'text-gray-200 cursor-not-allowed' : !cell.isCurrentMonth ? 'text-gray-300' : 'text-gray-700 hover:bg-blue-50'}
                        ${selected && !cellDisabled ? 'bg-blue-600 text-white hover:bg-blue-700 font-semibold' : ''}
                        ${todayCell && !selected ? 'font-bold ring-1 ring-blue-400' : ''}
                      `}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>

              {/* Today shortcut */}
              <div className="mt-2 pt-2 border-t border-gray-100 flex justify-center">
                <Button variant="link" size="xs" onClick={() => {
                    onChange(toISO(today.year, today.month, today.day));
                    setViewYear(today.year);
                    setViewMonth(today.month);
                    close();
                  }}>
                  Today
                </Button>
              </div>
            </PopoverPanel>
          </>
        )}
      </Popover>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
